<script lang="ts" setup>
import { useTaskStore } from "@core/stores/task.store"

import { computed } from "vue"

import { TASK_STATE } from "@core/constants"
import { computedEager } from "@vueuse/core"

import { useResponsive } from "@core/composables"

import { useConfirm } from "primevue/useconfirm"
import { useI18n } from "vue-i18n"

const taskStore = useTaskStore()

const confirm = useConfirm()

const { isMobile } = useResponsive()

const { t } = useI18n({
    messages: {
        fr: {
            title: "Annuler l'analyse",
            content: "Êtes-vous sûr de vouloir annuler l'analyse ?",
            confirm: "Confirmer",
            cancel: "Annuler",
        },
    },
})

const getAnalyseTask = computedEager(() => {
    return taskStore.getTaskByTaskname("get_compare_graph_by_building_ids")
})

const STATE = {
    WAITING: "waiting",
    PROCESSING: "processing",
    FINISHED: "finished",
    FAILURE: "failure",
}

const activeStep = computed(() => {
    switch (getAnalyseTask.value?.state) {
        case TASK_STATE.RECEIVED:
        case TASK_STATE.EMPTY:
            return 1
        case TASK_STATE.PROGRESS:
            return 2
        case TASK_STATE.SUCCESS:
        case TASK_STATE.FAILURE:
            return 3
        default:
            return 1
    }
})

const getStepList = computed(() => {
    return [
        {
            step: "task-started",
            message: "Prise en charge de la demande",
            status: () => {
                if (activeStep.value === 1) {
                    return STATE.PROCESSING
                }
                return STATE.FINISHED
            },
        },
        {
            step: "task-progress",
            message: "Analyse des données",
            status: () => {
                if (activeStep.value === 2) {
                    return STATE.PROCESSING
                } else if (activeStep.value > 2) {
                    return STATE.FINISHED
                }
                return STATE.WAITING
            },
            tasks: [
                {
                    step: "get_data_many_buildings_many_periods",
                    message: "Récupération des données des bâtiments",
                    status: getProgressTaskStatus(
                        "get_data_many_buildings_many_periods"
                    ),
                },
                {
                    step: "create_chart_compare_graph",
                    message: "Création du graphique",
                    status: getProgressTaskStatus("create_chart_compare_graph"),
                },
                {
                    step: "task_route_async",
                    message: "Sauvegarde des données",
                    status: getProgressTaskStatus("task_route_async"),
                },
            ],
        },
        {
            step: "task-finished",
            message: "Préparation des résultats",
            status: () => {
                if (activeStep.value === 3) {
                    return getAnalyseTask.value?.state === TASK_STATE.SUCCESS
                        ? STATE.FINISHED
                        : STATE.FAILURE
                }
                return STATE.WAITING
            },
            tasks: [
                {
                    step: "fetch_result_file",
                    message: "Préparation des résultats",
                    status: getProgressTaskStatus("fetch_result_file"),
                },
            ],
        },
    ]
})

function getProgressTaskStatus(sub_taskname: string) {
    // Cherche dans les progress_tasks si la tâche est terminée
    const task = getAnalyseTask.value?.progress_tasks.find(
        (task) => task.sub_taskname === sub_taskname
    )

    if (!task) {
        return STATE.WAITING
    }

    switch (task.state) {
        case TASK_STATE.STARTED:
        case TASK_STATE.PROGRESS:
            return STATE.PROCESSING
        case TASK_STATE.SUCCESS:
            return STATE.FINISHED
        case TASK_STATE.FAILURE:
            return STATE.FAILURE
        default:
            return STATE.WAITING
    }
}

function getTaskIconByStatus(status: string) {
    switch (status) {
        case STATE.PROCESSING:
            return "pi pi-spin pi-spinner"
        case STATE.FINISHED:
            return "pi pi-check"
        case STATE.FAILURE:
            return "pi pi-times"
        default:
            return "pi pi-pause"
    }
}

function getStepColorByStatus(status: string) {
    switch (status) {
        case STATE.PROCESSING:
            return "var(--p-primary)"
        case STATE.FINISHED:
            return "var(--p-teal-500)"
        case STATE.FAILURE:
            return "var(--p-red-500)"
        default:
            return ""
    }
}

function onClickCancelTask() {
    if (!getAnalyseTask.value) {
        return
    }
    taskStore.removeTask(getAnalyseTask.value?.task_id)
}

const confirm1 = (event: any) => {
    confirm.require({
        target: event.currentTarget,
        message: t("content"),
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: t("cancel"),
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            label: t("confirm"),
        },
        accept: () => {
            onClickCancelTask()
        },
        reject: () => {},
    })
}
</script>

<template>
    <div class="dashboard-card-compare-task">
        <BaseStepper v-model:value="activeStep" linear v-if="!isMobile">
            <BaseStepList>
                <BaseStep
                    v-for="(step, index) of getStepList"
                    :key="index"
                    v-slot="{ activateCallback, value, a11yAttrs }"
                    as-child
                    :value="index + 1"
                >
                    <div
                        class="dashboard-card-compare-task__step"
                        v-bind="a11yAttrs.root"
                    >
                        <button
                            class="bg-transparent border-0 inline-flex flex-col gap-2"
                            @click="activateCallback"
                            v-bind="a11yAttrs.header"
                        >
                            <span
                                class="rounded-full border-2 w-8 h-8 inline-flex items-center justify-center"
                            >
                                <BaseIcon
                                    class="dashboard-card-compare-task-icon"
                                    v-if="step.status() !== STATE.WAITING"
                                    :color="getStepColorByStatus(step.status())"
                                    :icon="getTaskIconByStatus(step.status())"
                                />
                                <span v-else>{{ index + 1 }}</span>
                            </span>
                        </button>
                        <p
                            class="text-sm font-bold text-primary whitespace-nowrap"
                        >
                            {{ step.message }}
                        </p>
                        <BaseDivider
                            class="dashboard-card-compare-task__divider"
                            v-if="index < getStepList.length - 1"
                            :style="{
                                '--p-divider-border-color':
                                    value === activeStep
                                        ? 'var(--p-primary)'
                                        : 'var(--p-surface-200)',
                            }"
                        >
                        </BaseDivider>
                    </div>
                </BaseStep>
            </BaseStepList>
            <BaseStepPanels>
                <DashboardCardCompareTaskItem
                    v-for="(step, index) of getStepList"
                    :key="index"
                    :value="index + 1"
                    :tasks="step.tasks"
                    :active-step="activeStep"
                />
            </BaseStepPanels>
        </BaseStepper>
        <BaseStepper
            v-else
            v-model:value="activeStep"
            class="dashboard-card-compare-task__stepper-mobile"
        >
            <BaseStepItem
                v-for="(step, index) of getStepList"
                :key="index"
                :value="index + 1"
            >
                <BaseStep v-slot="{ activateCallback, a11yAttrs }" as-child>
                    <div
                        class="dashboard-card-compare-task__step dashboard-card-compare-task__step--mobile"
                        v-bind="a11yAttrs.root"
                    >
                        <button
                            class="bg-transparent border-0 inline-flex flex-col gap-2"
                            @click="activateCallback"
                            v-bind="a11yAttrs.header"
                        >
                            <span
                                class="rounded-full border-2 w-8 h-8 inline-flex items-center justify-center"
                            >
                                <BaseIcon
                                    class="dashboard-card-compare-task-icon"
                                    v-if="step.status() !== STATE.WAITING"
                                    :color="getStepColorByStatus(step.status())"
                                    :icon="getTaskIconByStatus(step.status())"
                                />
                                <span v-else>{{ index + 1 }}</span>
                            </span>
                        </button>
                        <p
                            class="text-sm font-bold text-primary whitespace-nowrap"
                        >
                            {{ step.message }}
                        </p>
                    </div>
                </BaseStep>
                <DashboardCardCompareTaskItem
                    :key="index"
                    :value="index + 1"
                    :active-step="activeStep"
                    vertical
                    :tasks="step.tasks"
                />
            </BaseStepItem>
        </BaseStepper>
        <BaseButton
            severity="warn"
            class="ml-auto"
            outlined
            @click="confirm1($event)"
            >{{ t("title") }}</BaseButton
        >
        <BaseConfirmPopup></BaseConfirmPopup>
    </div>
</template>

<style scoped>
.dashboard-card-compare-task {
    display: flex;
    flex-direction: column;
    padding: 2rem;
    .dashboard-card-compare-task__subtask-list {
        display: flex;
        flex-direction: column;
        gap: 1rem;

        border-radius: 0.375rem;
        border: 1px solid #e0e0e0;
        background: #fff;
        padding: 1.5rem;
    }
    .dashboard-card-compare-task__subtask-item {
        display: flex;
        gap: 1rem;
        white-space: nowrap;
        align-items: center;

        --p-progressbar-height: 1rem;

        .dashboard-card-compare-task__subtask-item-header {
            display: flex;
            gap: 1rem;
            align-items: center;
        }
    }

    .dashboard-card-compare-task__subtask-item-inactive {
        padding-left: 2rem;
        color: #9e9e9e;
    }
}

.dashboard-card-compare-task__step {
    --p-divider-horizontal-padding: 0.5rem 1rem;
    display: flex;
    flex-direction: row;
    flex: 1 1 0;
    gap: 0.5rem;
    align-items: center;

    &:nth-child(2) {
        padding: 0 1rem;
    }
    &:nth-child(3) {
        max-width: min-content;
    }
}

.dashboard-card-compare-task__step--mobile {
    padding: 0.5rem 0;
}

.dashboard-card-compare-task-icon {
    transition: background-color 2s ease-in-out;
}

.dashboard-card-compare-task__stepper-mobile {
    --p-stepper-separator-margin: 0 0 0 1rem;
}
</style>
